import { ErrorBoundary, Provider } from '@rollbar/react'
import React, { ReactElement, ReactNode, useEffect } from 'react'
import { rollbarConfig } from 'configs/rollbar'
import { NextPage } from 'next'
import { appWithTranslation } from 'next-i18next'
import type { AppProps } from 'next/app'
import { Router } from 'next/router'
import NProgress from 'nprogress'
import { SWRConfig } from 'swr'
import Toast from 'shared/components/toast'
import { FeatureFlags, FeatureFlagsProvider } from 'shared/contexts/feature-flags-context'
import { getCommunityLayout } from 'modules/community/components/community-layout'
import NextI18nextConfig from '../../next-i18next.config'
import '../../styles/globals.css'
import 'nprogress/nprogress.css'

export type NextPageWithLayout = NextPage & {
  getLayout: (page: ReactElement, props: AppProps['pageProps']) => ReactNode
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
  pageProps: AppProps['pageProps'] & {
    featureFlags?: FeatureFlags
  }
}

function App({ Component, pageProps }: AppPropsWithLayout) {
  useEffect(() => {
    const handleRouteStart = () => NProgress.start()
    const handleRouteDone = () => NProgress.done()

    Router.events.on('routeChangeStart', handleRouteStart)
    Router.events.on('routeChangeComplete', handleRouteDone)
    Router.events.on('routeChangeError', handleRouteDone)

    return () => {
      // Make sure to remove the event handler on unmount!
      Router.events.off('routeChangeStart', handleRouteStart)
      Router.events.off('routeChangeComplete', handleRouteDone)
      Router.events.off('routeChangeError', handleRouteDone)
    }
  }, [])

  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? getCommunityLayout

  const featureFlags: FeatureFlags = pageProps.featureFlags || null

  return (
    <Provider config={rollbarConfig}>
      <ErrorBoundary>
        <FeatureFlagsProvider flags={featureFlags}>
          <SWRConfig
            value={{
              revalidateOnFocus: false,
              revalidateIfStale: false,
              shouldRetryOnError: false,
            }}
          >
            {getLayout(<Component {...pageProps} />, pageProps)}
            <Toast />
          </SWRConfig>
        </FeatureFlagsProvider>
      </ErrorBoundary>
    </Provider>
  )
}

// @ts-ignore
export default appWithTranslation(App, NextI18nextConfig)
